import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import { Expense_Entity } from "../../entities/expense";
import { filmsApi } from "../api/filmsApi";
import { scheduleInvalidation } from "../api/deferredInvalidation";

const expensesAdapter = createEntityAdapter<Expense_Entity>();
const initialState = expensesAdapter.getInitialState();

export const expenseEndpoints = filmsApi.injectEndpoints({
  endpoints: (build) => ({
    getExpenses: build.query<
      EntityState<Expense_Entity>,
      Record<string, unknown> | void
    >({
      query: (args) => {
        return {
          url: `expenses`,
          params: args,
        };
      },
      transformResponse: (responseData: Expense_Entity[]) => {
        return expensesAdapter.setAll(initialState, responseData);
      },
      providesTags: ["expenses"],
    }),
    getExpense: build.query<Expense_Entity, number>({
      query: (id) => `expenses/${id}`,
      providesTags: (result, error, id) => [{ type: "expenses", id }],
    }),
    createExpense: build.mutation<Expense_Entity, Partial<Expense_Entity>>({
      query: (body) => ({
        method: "POST",
        body,
        url: `expenses`,
      }),
      invalidatesTags: ["expenses"],
    }),
    updateExpense: build.mutation<
      void,
      { id: number; body: Partial<Expense_Entity> }
    >({
      query: (args) => ({
        method: "PUT",
        body: args.body,
        url: `expenses/${args.id}`,
      }),
      // Deliberately no invalidatesTags — refreshing per cell rebuilt the grid
      // mid-typing and dropped input. See scheduleInvalidation.
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // The grid reverts the cell and reports it; still refresh so the row
          // settles on what the server actually holds.
        }
        scheduleInvalidation(dispatch, ["expenses"]);
      },
    }),
    deleteExpense: build.mutation<void, number>({
      query: (id) => ({
        method: "DELETE",
        url: `expenses/${id}`,
      }),
      invalidatesTags: ["expenses"],
    }),
  }),
});

export const {
  useGetExpenseQuery,
  useGetExpensesQuery,
  useCreateExpenseMutation,
  useDeleteExpenseMutation,
  useUpdateExpenseMutation,
} = expenseEndpoints;

export default expenseEndpoints;
